import React, { useState, useEffect, useMemo } from 'react'
import Chart from 'react-apexcharts'
import classNames from 'classnames'
import { useDispatch, useSelector } from 'react-redux'

import classes from './completionRate.module.css'
import { getCompletionRate } from '../dashboardApi'
import { dashboardSelectors } from '../dashboardSlice'
import notification from '../../../components/Notification/notification'
import Spinner from '../../../components/loaders/Spinner'

const CompletionRate = () => {
    const dispatch = useDispatch()
    const [completionRate, setCompletionRate] = useState(null)
    const completionRateData = useSelector(dashboardSelectors.completionRate)
        .data
    const isFetchingCompletionRate = useSelector(dashboardSelectors.isFetching)
        .completionRate
    const completionRateErrorMessage = useSelector(
        dashboardSelectors.errorMessage
    ).completionRate
    const completionRateHasError = useSelector(dashboardSelectors.hasError)
        .completionRate
    const indicatorColors = [
        '#8C2C00',
        '#C7460A',
        '#9D6349',
        '#501900',
        '#EB6324',
    ]

    useEffect(() => {
        dispatch(getCompletionRate({ filterParam: completionRate }))
    }, [completionRate])

    useEffect(() => {
        if (completionRateHasError) {
            notification.failure({
                title: 'Failed to load the completion rate',
                description: completionRateErrorMessage,
            })
        }
    }, [completionRateHasError])

    const series = useMemo(() => {
        return [
            {
                data:
                    completionRateData?.top5Courses?.map(
                        item => item.value || 0
                    ) || [],
            },
        ]
    }, [completionRateData])

    const keyMap = useMemo(() => {
        return (
            completionRateData?.top5Courses?.map((item, index) => ({
                name: item.courseCode,
                color: indicatorColors[index],
            })) || []
        )
    }, [completionRateData])

    const options = {
        chart: {
            id: 'completion-rate',
            toolbar: {
                show: false,
            },
            type: 'bar',
        },
        colors: indicatorColors,
        fill: {
            type: 'solid',
        },
        xaxis: {
            categories:
                completionRateData?.top5Courses?.map(item => item.courseCode) ||
                [],
            labels: {
                show: false,
            },
            tickPlacement: 'on',
        },
        dataLabels: {
            enabled: false,
        },
        stroke: {
            width: 1,
        },
        plotOptions: {
            bar: {
                horizontal: false,
                distributed: true,
            },
        },
        legend: {
            position: 'top',
            horizontalAlign: 'right',
            offsetY: -30,
        },
        tooltip: {
            enabled: true,
            enabledOnSeries: undefined,
            shared: true,
        },
    }

    const renderKeyMap = () => {
        return (
            <div className={classes.keymapContainer}>
                <p className={classes.title}>Key Map</p>
                <div className="row">
                    {keyMap.map(item => (
                        <div key={item.name} className="col-sm-6">
                            <div className="d-flex align-items-center">
                                <div
                                    className={classes.indicator}
                                    style={{ backgroundColor: item.color }}
                                />
                                <span className={classes.indicatorText}>
                                    {item.name}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    return (
        <div className={classes.container}>
            {isFetchingCompletionRate ? (
                <Spinner />
            ) : (
                <>
                    <div
                        className={classNames(
                            'row d-flex justify-content-between',
                            classes.header
                        )}
                    >
                        <div className="col">
                            <span className={classes.title}>
                                Completion Rate
                            </span>
                        </div>
                        <div
                            className="col"
                            style={{ flex: 'auto', maxWidth: 'max-content' }}
                        >
                            <select
                                class="form-select"
                                onChange={ev =>
                                    setCompletionRate(ev.target.value)
                                }
                            >
                                <option value="Highest Completion">
                                    Highest Completion
                                </option>
                                <option value="Lowest Completion">
                                    Lowest Completion
                                </option>
                                <option value="Highest Time Spent">
                                    Highest Time Spent
                                </option>
                                <option value="Lowest Time Spent">
                                    Lowest Time Spent
                                </option>
                            </select>
                        </div>
                    </div>
                    <Chart
                        options={options}
                        series={series}
                        type="bar"
                        width={'100%'}
                        height={320}
                    />
                    {renderKeyMap()}
                </>
            )}
        </div>
    )
}

export default CompletionRate
